import { createElement, type CSSProperties, type ReactNode } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Info, XCircle } from "lucide-react";

type NotificationType = "info" | "error" | "success" | "warning";

type SetErrorFn = (messageOrName: string, maybeError?: unknown) => void;

type SetLoadingFn = (loading: boolean) => void;

type SessionLike =
  | {
      user?: ({ accessToken?: string | null } & Record<string, unknown>) | null;
      accessToken?: string | null;
    }
  | null
  | undefined;

export const getToken = (session: SessionLike): string | null => {
  return session?.user?.accessToken ?? session?.accessToken ?? null;
};

export type Notice = { type: "error" | "success"; text: string };

export function updateNotice(
  setNotice: ((n: Notice | null) => void) | undefined,
  type: Notice["type"],
  text: string,
): void {
  if (typeof setNotice !== "function") return;
  try {
    setNotice({ type, text });
  } catch {
    /* no-op */
  }
}

export const handleError = (
  err: unknown,
  defaultMessage?: string,
  setError?: SetErrorFn,
  setLoading?: SetLoadingFn,
  setNotice?: (n: Notice | null) => void,
  showToast: boolean = true,
): string => {
  console.error("Error:", err);
  const errorWithResponse = err as {
    response?: { data?: Record<string, unknown> };
    message?: unknown;
  };
  const res = errorWithResponse.response;
  const body: Record<string, unknown> = res?.data || {};
  const rawMessage = (
    (body.message as unknown) ||
    (body.error as unknown) ||
    errorWithResponse.message ||
    ""
  ).toString();
  const message = rawMessage || defaultMessage || "Ocurrió un error inesperado";

  if (typeof setError === "function") {
    try {
      if (setError.length >= 2) {
        setError("root", { type: "server", message });
      } else {
        setError(message);
      }
    } catch {
      try {
        setError(message);
      } catch {
        /* no-op */
      }
    }
  }

  updateNotice(setNotice, "error", message);

  if (typeof setLoading === "function") {
    try {
      setLoading(false);
    } catch {}
  }

  if (showToast) {
    try {
      handleNotification("error", "Error", message);
    } catch {
      /* no-op */
    }
  }

  return message;
};

const BASE_TOAST_STYLE: CSSProperties = {
  borderRadius: "12px",
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  border: "1px solid var(--color-border)",
  padding: "12px 14px",
  willChange: "opacity, background-size",
};

const NOTIFICATION_CONFIG: Record<
  NotificationType,
  { icon: ReactNode; style: CSSProperties }
> = {
  info: {
    icon: createElement(Info, {
      size: 18,
      strokeWidth: 2,
      color: "currentColor",
    }),
    style: {
      ...BASE_TOAST_STYLE,
      backgroundColor: "var(--color-card)",
      color: "var(--color-text)",
      borderLeft: "4px solid var(--color-secondary)",
    },
  },
  error: {
    icon: createElement(XCircle, {
      size: 18,
      strokeWidth: 2,
      color: "currentColor",
    }),
    style: {
      ...BASE_TOAST_STYLE,
      backgroundColor: "var(--color-card)",
      color: "var(--color-destructive)",
      borderLeft: "4px solid var(--color-destructive)",
    },
  },
  success: {
    icon: createElement(CheckCircle2, {
      size: 18,
      strokeWidth: 2,
      color: "currentColor",
    }),
    style: {
      ...BASE_TOAST_STYLE,
      backgroundColor: "var(--color-card)",
      color: "var(--color-text)",
      borderLeft: "4px solid var(--color-primary)",
    },
  },
  warning: {
    icon: createElement(AlertTriangle, {
      size: 18,
      strokeWidth: 2,
      color: "currentColor",
    }),
    style: {
      ...BASE_TOAST_STYLE,
      backgroundColor: "var(--color-card)",
      color: "var(--color-text)",
      borderLeft: "4px solid var(--color-chart-4)",
    },
  },
};

export function handleNotification(
  type: NotificationType = "info",
  title?: string,
  description?: string,
): void {
  const normalizedType = String(
    type || "info",
  ).toLowerCase() as NotificationType;

  title = title || "Título de notificación faltante";
  description = description || "";

  const cfg = NOTIFICATION_CONFIG[normalizedType] || NOTIFICATION_CONFIG.info;
  const duration = 5000;

  const progressColorVar = "var(--color-muted)";

  const baseBackgroundLayer =
    cfg.style.background || cfg.style.backgroundColor || "var(--color-surface)";

  const styleWithProgress: CSSProperties = {
    ...cfg.style,
    background: `linear-gradient(${progressColorVar}, ${progressColorVar}), ${baseBackgroundLayer}`,
    backgroundRepeat: "no-repeat, no-repeat",
    backgroundPosition: "bottom left, center",
    backgroundSize: `0% 3px, 100% 100%`,
    animation: `toastProgressFill ${duration}ms linear forwards, toastVisibility ${duration}ms cubic-bezier(0.22, 1, 0.36, 1) forwards`,
  };

  toast(title, {
    description,
    duration,
    icon: cfg.icon,
    style: styleWithProgress,
    position: "top-center",
  });
}
